import React from "react";
import Link from "next/link";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { ASSET_URL } from "../../../config";
import StarRating from "../components/StarRating";
import { walkMinutesFrom, getBethHabadLabel } from "../utils";

const detailStyles = `
/* Hero image */
.tr-adetail__hero {
  max-width: 1180px;
  margin: 24px auto 32px;
  padding: 0 24px;
  box-sizing: border-box;
  width: 100%;
}
.tr-adetail__hero-img {
  width: 100%;
  aspect-ratio: 16/7;
  object-fit: cover;
  border-radius: 32px;
  display: block;
  background: #e5e7eb;
}
.tr-adetail__hero-placeholder {
  width: 100%;
  aspect-ratio: 16/7;
  border-radius: 32px;
  background: linear-gradient(135deg, #e5e7eb, #cbd5e1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

/* Container */
.tr-adetail__body {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
  width: 100%;
}

/* Title */
.tr-adetail__title {
  font-size: clamp(26px, 4vw, 40px);
  font-weight: 800;
  color: #1a1a2e;
  margin: 0 0 12px;
  line-height: 1.2;
}
.tr-adetail__rating-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.tr-adetail__walk {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--primary, #1b579e);
}

/* Grid 2 colonnes */
.tr-adetail__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 380px);
  gap: 40px;
  align-items: start;
}
.tr-adetail__grid > * { min-width: 0; }
@media (max-width: 900px) {
  .tr-adetail__grid { grid-template-columns: 1fr; }
}

/* ── Colonne gauche ── */
.tr-adetail__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}
.tr-adetail__tag {
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 999px;
  background: #ffe8d9;
  color: #1a1a2e;
}
.tr-adetail__info {
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
  line-height: 1.5;
}
.tr-adetail__info b {
  color: #1a1a2e;
  font-weight: 700;
  margin-inline-end: 4px;
}

/* Boutons ronds action */
.tr-adetail__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 24px;
}
.tr-adetail__action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 500;
}
.tr-adetail__action-btn {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: color-mix(in srgb, var(--primary, #1b579e) 14%, white);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary, #1b579e);
  transition: transform .15s, box-shadow .15s;
}
.tr-adetail__action:hover .tr-adetail__action-btn {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px color-mix(in srgb, var(--primary, #1b579e) 25%, transparent);
}

/* Horaires */
.tr-adetail__hours-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 40px 0 14px;
}
.tr-adetail__hours {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  row-gap: 10px;
  column-gap: 12px;
  font-size: 14px;
  color: #374151;
  max-width: 100%;
  width: 420px;
}
@media (max-width: 480px) { .tr-adetail__hours { width: 100%; } }
.tr-adetail__hours-day {
  color: #6b7280;
  text-transform: lowercase;
}
.tr-adetail__hours-slots {
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: #1a1a2e;
}
.tr-adetail__hours-closed {
  color: #9ca3af;
  font-style: italic;
}

/* Rich description */
.tr-adetail__desc {
  margin-top: 18px;
  font-size: 15px;
  line-height: 1.7;
  color: #1f2937;
  max-width: 720px;
  overflow-wrap: anywhere;
}
.tr-adetail__desc img { max-width: 100%; height: auto; }
.tr-adetail__desc h1,
.tr-adetail__desc h2,
.tr-adetail__desc h3 { color: #1a1a2e; margin-top: 24px; margin-bottom: 10px; }
.tr-adetail__desc p { margin: 0 0 12px; }
.tr-adetail__desc a { color: var(--primary, #1b579e); text-decoration: underline; }

/* ── Colonne droite : carte infos ── */
.tr-adetail__side {
  background: white;
  border-radius: 24px;
  padding: 22px 24px;
  box-shadow: 0 8px 24px rgba(0,0,0,.06), 0 1px 4px rgba(0,0,0,.04);
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 88px;
  max-width: 100%;
  box-sizing: border-box;
}
.tr-adetail__side-row {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: #374151;
  font-size: 14px;
  line-height: 1.4;
  min-width: 0;
}
.tr-adetail__side-row span:last-child { min-width: 0; overflow-wrap: anywhere; }
.tr-adetail__side-row:hover { color: #1a1a2e; }
.tr-adetail__side-icon {
  width: 40px; height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: color-mix(in srgb, var(--primary, #1b579e) 14%, white);
  color: var(--primary, #1b579e);
}

/* Back link */
.tr-adetail__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--primary, #1b579e);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 18px;
  text-decoration: none;
}
.tr-adetail__back:hover { text-decoration: underline; }
`;

const DAYS_ORDER = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function ActionButton({ type, href, label, icon, external }) {
    return (
        <a
            href={href}
            className={`tr-adetail__action tr-adetail__action--${type}`}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
        >
            <span className="tr-adetail__action-btn">
                {renderIcon(icon, 26)}
            </span>
            <span>{label}</span>
        </a>
    );
}

export default function AttractionDetail({ context, loading }) {
    const { org, travel } = context;

    const placeId = context.route?.[2];
    const rawPlace = (travel?.attractionPlaces || []).find(p => p.id === placeId);

    const DAY_LABELS = {
        sunday: t`dimanche`,
        monday: t`lundi`,
        tuesday: t`mardi`,
        wednesday: t`mercredi`,
        thursday: t`jeudi`,
        friday: t`vendredi`,
        saturday: t`samedi`,
    };

    const bethHabadLabel = getBethHabadLabel(travel, context.lang);

    if (!rawPlace) {
        return (
            <div className="travel-page travel-page--attractions-detail">
                <Meta context={context} title={org.name} description={org.name} />
                {loading && <Loader />}
                <Header context={context} />
                <main className="travel-main">
                    <div className="travel-container" style={{ padding: "80px 24px", textAlign: "center" }}>
                        <h1><Trans>Attraction introuvable</Trans></h1>
                        <Link href={org.BASE_WEB_URL + "/travel/attractions"} legacyBehavior>
                            <a className="tr-adetail__back">
                                {renderIcon({ name: "IconArrowLeft", source: "tabler" }, 16)}
                                <Trans>Retour à la liste</Trans>
                            </a>
                        </Link>
                    </div>
                </main>
                <Footer context={context} />
            </div>
        );
    }

    const place = applyLang(rawPlace, context.lang);
    const categories = (travel?.attraction?.categories || []).map(c => applyLang(c, context.lang));
    const category = categories.find(c => c.id === place.categoryId) || null;

    const imgSrc = place.image ? `${ASSET_URL}${place.image}` : null;
    const address = place.address?.address;
    const lat = place.address?.lat;
    const lon = place.address?.lon;
    const navUrl = lat != null && lon != null
        ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`
        : (address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null);

    const walkMinutes = walkMinutesFrom(org, place);

    const contacts = place.contacts || [];
    const phoneContact = contacts.find(c => c.type === "phone");
    const emailContact = contacts.find(c => c.type === "email");
    const whatsappContact = contacts.find(c => c.type === "whatsapp");

    const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, "").trim();
    const placeDesc = stripHtml(place.description) || t`${place.name} — à découvrir à ${org.ville || org.name}`;

    const DAY_MAP = { sunday: "Sunday", monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday" };
    const openingHours = [];
    if (place.hours) {
        for (const [day, slots] of Object.entries(place.hours)) {
            if (!Array.isArray(slots)) continue;
            for (const s of slots) {
                if (!s.open || !s.close) continue;
                openingHours.push({
                    "@type": "OpeningHoursSpecification",
                    dayOfWeek: DAY_MAP[day] || day,
                    opens: s.open,
                    closes: s.close,
                });
            }
        }
    }

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "TouristAttraction",
        name: place.name,
        description: placeDesc,
        image: imgSrc || undefined,
        address: address ? { "@type": "PostalAddress", streetAddress: address } : undefined,
        geo: (lat != null && lon != null) ? {
            "@type": "GeoCoordinates",
            latitude: lat,
            longitude: lon,
        } : undefined,
        telephone: phoneContact?.value,
        email: emailContact?.value,
        url: place.website || undefined,
        aggregateRating: place.note ? {
            "@type": "AggregateRating",
            ratingValue: place.note,
            bestRating: 5,
            ratingCount: 1,
        } : undefined,
        openingHoursSpecification: openingHours.length > 0 ? openingHours : undefined,
    };

    return (
        <div className="travel-page travel-page--attractions-detail tr-adetail">
            <style dangerouslySetInnerHTML={{ __html: detailStyles }} />
            <Meta
                context={context}
                title={place.name}
                description={placeDesc.slice(0, 200)}
                image={imgSrc}
                jsonLd={jsonLd}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">

                {/* Hero image */}
                <div className="tr-adetail__hero">
                    {imgSrc ? (
                        <img src={imgSrc} alt={place.name} className="tr-adetail__hero-img" />
                    ) : (
                        <div className="tr-adetail__hero-placeholder">
                            {renderIcon({ name: "IconCamera", source: "tabler" }, 64)}
                        </div>
                    )}
                </div>

                <div className="tr-adetail__body">

                    <Link href={org.BASE_WEB_URL + "/travel/attractions"} legacyBehavior>
                        <a className="tr-adetail__back">
                            {renderIcon({ name: "IconArrowLeft", source: "tabler" }, 16)}
                            <Trans>Retour</Trans>
                        </a>
                    </Link>

                    <h1 className="tr-adetail__title">{place.name}</h1>

                    {(place.note || walkMinutes != null) && (
                        <div className="tr-adetail__rating-row">
                            <StarRating value={place.note} size={20} showValue />
                            {walkMinutes != null && (
                                <span className="tr-adetail__walk">
                                    {renderIcon({ name: "IconWalk", source: "tabler" }, 18)}
                                    <Trans>{walkMinutes} min du {bethHabadLabel}</Trans>
                                </span>
                            )}
                        </div>
                    )}

                    <div className="tr-adetail__grid">

                        {/* Colonne gauche */}
                        <div>
                            <div className="tr-adetail__tags">
                                {category?.label && (
                                    <span className="tr-adetail__tag">{category.label}</span>
                                )}
                            </div>

                            {place.price && (
                                <div className="tr-adetail__info">
                                    <b><Trans>Tarif :</Trans></b>{place.price}
                                </div>
                            )}

                            {/* Description HTML */}
                            {place.description && (
                                <div
                                    className="tr-adetail__desc"
                                    dangerouslySetInnerHTML={{ __html: place.description }}
                                />
                            )}

                            {/* Actions */}
                            <div className="tr-adetail__actions">
                                {phoneContact && (
                                    <ActionButton
                                        type="phone"
                                        href={`tel:${phoneContact.value}`}
                                        label={t`Appel`}
                                        icon={{ name: "IconPhone", source: "tabler" }}
                                    />
                                )}
                                {navUrl && (
                                    <ActionButton
                                        type="nav"
                                        href={navUrl}
                                        label={t`Naviguer`}
                                        icon={{ name: "IconMapPin", source: "tabler" }}
                                        external
                                    />
                                )}
                                {whatsappContact && (
                                    <ActionButton
                                        type="whatsapp"
                                        href={`https://wa.me/${whatsappContact.value.replace(/\D/g, "")}`}
                                        label="WhatsApp"
                                        icon={{ name: "IconBrandWhatsapp", source: "tabler" }}
                                        external
                                    />
                                )}
                                {emailContact && (
                                    <ActionButton
                                        type="email"
                                        href={`mailto:${emailContact.value}`}
                                        label={t`E-mail`}
                                        icon={{ name: "IconMail", source: "tabler" }}
                                    />
                                )}
                                {place.website && (
                                    <ActionButton
                                        type="website"
                                        href={place.website}
                                        label={t`Site web`}
                                        icon={{ name: "IconWorld", source: "tabler" }}
                                        external
                                    />
                                )}
                            </div>

                            {/* Horaires */}
                            {place.hours && Object.values(place.hours).some(slots => slots?.length > 0) && (
                                <>
                                    <h3 className="tr-adetail__hours-title">
                                        <Trans>Horaires d'ouverture</Trans>
                                    </h3>
                                    <div className="tr-adetail__hours">
                                        {DAYS_ORDER.map(day => {
                                            const slots = place.hours[day] || [];
                                            return (
                                                <React.Fragment key={day}>
                                                    <div className="tr-adetail__hours-day">{DAY_LABELS[day]}</div>
                                                    <div className="tr-adetail__hours-slots">
                                                        {slots.length === 0 ? (
                                                            <span className="tr-adetail__hours-closed">
                                                                <Trans>Fermé</Trans>
                                                            </span>
                                                        ) : (
                                                            slots.map((s, i) => (
                                                                <span key={i}>{s.open}-{s.close}</span>
                                                            ))
                                                        )}
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                        </div>

                        {/* Colonne droite : carte latérale */}
                        <aside className="tr-adetail__side">
                            {address && (
                                <a
                                    href={navUrl || "#"}
                                    className="tr-adetail__side-row"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span className="tr-adetail__side-icon">
                                        {renderIcon({ name: "IconMapPin", source: "tabler" }, 20)}
                                    </span>
                                    <span>{address}</span>
                                </a>
                            )}
                            {phoneContact && (
                                <a href={`tel:${phoneContact.value}`} className="tr-adetail__side-row">
                                    <span className="tr-adetail__side-icon">
                                        {renderIcon({ name: "IconPhone", source: "tabler" }, 20)}
                                    </span>
                                    <span>{phoneContact.value}</span>
                                </a>
                            )}
                            {whatsappContact && (
                                <a
                                    href={`https://wa.me/${whatsappContact.value.replace(/\D/g, "")}`}
                                    className="tr-adetail__side-row"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span className="tr-adetail__side-icon">
                                        {renderIcon({ name: "IconBrandWhatsapp", source: "tabler" }, 20)}
                                    </span>
                                    <span>{whatsappContact.value}</span>
                                </a>
                            )}
                            {emailContact && (
                                <a href={`mailto:${emailContact.value}`} className="tr-adetail__side-row">
                                    <span className="tr-adetail__side-icon">
                                        {renderIcon({ name: "IconMail", source: "tabler" }, 20)}
                                    </span>
                                    <span>{emailContact.value}</span>
                                </a>
                            )}
                            {place.website && (
                                <a href={place.website} className="tr-adetail__side-row" target="_blank" rel="noopener noreferrer">
                                    <span className="tr-adetail__side-icon">
                                        {renderIcon({ name: "IconWorld", source: "tabler" }, 20)}
                                    </span>
                                    <span><Trans>Site web</Trans></span>
                                </a>
                            )}
                        </aside>

                    </div>

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
